import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { formatINR, useSubscription } from "@/lib/subscription";
import { marketingUrl } from "@/lib/domains";
import { Users, Calendar, Receipt, Sparkles, MessageCircle, FileSignature, ShieldCheck, IndianRupee, Clock, ArrowRight, Check } from "lucide-react";

const METRICS = [
  { key: "patients_added",         label: "Patients added",         icon: Users },
  { key: "appointments_managed",   label: "Appointments managed",   icon: Calendar },
  { key: "invoices_generated",     label: "Invoices generated",     icon: Receipt },
  { key: "ai_requests_used",       label: "AI requests used",       icon: Sparkles },
  { key: "whatsapp_messages_sent", label: "WhatsApp messages sent", icon: MessageCircle },
  { key: "consent_forms_created",  label: "Consent forms created",  icon: FileSignature },
  { key: "warranty_cards_issued",  label: "Warranty cards issued",  icon: ShieldCheck },
  { key: "time_saved_hours",       label: "Hours saved (est.)",     icon: Clock, suffix: " hrs" },
];

export default function TrialEnded() {
  const nav = useNavigate();
  const { sub, refresh } = useSubscription();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    api.get("/subscription/trial-summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/subscription/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  // If they somehow arrive here on an active paid plan, kick back to app
  useEffect(() => {
    if (sub && sub.active && sub.plan?.id !== "trial") nav("/app", { replace: true });
  }, [sub, nav]);

  const choose = async (planId) => {
    await openRazorpayCheckout({ lookup_key: planId });
    refresh();
  };

  const filteredPlans = plans.filter((p) => p.interval === cycle);
  if (!data) return <div className="min-h-screen bg-linen grid place-items-center text-ink-soft">Loading your trial summary…</div>;

  return (
    <div className="min-h-screen bg-linen text-ink">
      <header className="container-lg py-6 flex items-center justify-between">
        <a href={marketingUrl("/")} className="flex items-center gap-2">
          <img src="/viyora-icon-64.png" alt="Viyora" className="w-9 h-9 " />
          <span className="font-display text-xl tracking-tight">Viyora</span>
        </a>
        <button onClick={() => { localStorage.clear(); nav("/login"); }} className="text-xs text-ink-mid hover:text-ink">Sign out</button>
      </header>

      <main className="container-lg py-8 max-w-5xl">
        <span className="overline text-ink-soft">Your 30-day trial has ended</span>
        <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tight">Here&apos;s what you built with Viyora.</h1>
        <p className="mt-3 text-[15px] text-ink-mid max-w-2xl">In 30 days, your clinic used Viyora to do this much work. Pick a plan to keep it going — none of your data is lost.</p>

        {/* Metric grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          {METRICS.map((m) => (
            <div key={m.key} className="card-widget" data-testid={`metric-${m.key}`}>
              <m.icon className="w-4 h-4 text-ink-mid" />
              <div className="mt-3 font-display text-3xl">
                {Number(data[m.key] || 0).toLocaleString("en-IN")}{m.suffix || ""}
              </div>
              <div className="mt-1 text-xs uppercase tracking-widest text-ink-soft">{m.label}</div>
            </div>
          ))}
        </div>

        {data.revenue_recorded > 0 && (
          <div className="mt-6 relative overflow-hidden rounded-2xl border border-[#F2E5DF] bg-white shadow-[0_1px_2px_rgba(17,24,39,0.03),0_8px_24px_-16px_rgba(245,83,45,0.18)] p-6" data-testid="revenue-highlight">
            {/* Thin orange accent border on top */}
            <div className="absolute inset-x-0 top-0 h-[3px] bg-pine" />
            {/* Subtle orange highlight strip behind the figure */}
            <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-[#FFF2EC] via-[#FFF8F4] to-transparent pointer-events-none" />
            <div className="relative flex items-center gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-2xl bg-pine/10 text-pine grid place-items-center shrink-0">
                <IndianRupee className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="text-xs uppercase tracking-[0.14em] font-semibold text-ink-soft">Revenue recorded during trial</div>
                <div className="mt-1 text-5xl font-extrabold text-ink tracking-tight tabular-nums">
                  ₹{Number(data.revenue_recorded).toLocaleString("en-IN")}
                </div>
                <div className="text-sm text-ink-mid font-medium mt-2 max-w-md leading-relaxed">
                  You&apos;ve already made this much using Viyora. Continuing your plan pays for itself in a single afternoon.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plan choice */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl md:text-3xl tracking-tight text-ink">Choose a plan to continue</h2>
            <div className="inline-flex rounded-full border border-[#F2E5DF] p-1 bg-white">
              <button onClick={() => setCycle("monthly")} className={`px-4 py-1.5 text-xs rounded-full transition ${cycle === "monthly" ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"}`}>Monthly</button>
              <button onClick={() => setCycle("yearly")}  className={`px-4 py-1.5 text-xs rounded-full transition ${cycle === "yearly"  ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"}`}>Yearly · Save 16%</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredPlans.map((p) => {
              const featured = p.tier === "clinic";
              const tagline = p.tier === "clinic"
                ? "For growing multi-chair practices scaling operations."
                : "For solo dentists running a modern, calm clinic.";
              const highlights = p.tier === "clinic"
                ? ["Viyora Copilot · 200 requests / month", "WhatsApp automation · 500 messages / month", "Up to 8 chairs · multi-doctor scheduling", "Google Reviews · Inventory · Advanced reports"]
                : ["Everything a solo practice needs", "1 chair · 1 doctor · 1 receptionist", "GST invoicing · online booking · consent + warranty", "Razorpay integrated · WhatsApp reminders"];
              const savings = p.interval === "yearly" ? "Save 16% vs monthly" : null;

              return (
                <div key={p.id} data-testid={`trial-end-plan-${p.id}`}
                     className={`relative overflow-hidden rounded-2xl bg-white p-8 transition duration-200 hover:-translate-y-1 flex flex-col ${
                       featured
                         ? "ring-2 ring-pine shadow-[0_1px_2px_rgba(17,24,39,0.04),0_24px_60px_-28px_rgba(245,83,45,0.35)] hover:shadow-[0_1px_2px_rgba(17,24,39,0.04),0_32px_72px_-24px_rgba(245,83,45,0.45)]"
                         : "border border-[#E5E7EB] shadow-[0_1px_2px_rgba(17,24,39,0.04)] hover:shadow-[0_20px_48px_-24px_rgba(17,24,39,0.18)] hover:border-ink-faint"
                     }`}>
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.14em] font-semibold text-ink-soft">
                        {p.tier === "clinic" ? "Clinic" : "Solo"}
                      </div>
                      <div className="mt-2 text-2xl font-bold text-ink tracking-tight">
                        {p.name.replace(" (Yearly)", "")}
                      </div>
                    </div>
                    {featured && (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-pine text-white font-bold shadow-sm">
                        Most popular
                      </span>
                    )}
                  </div>

                  <p className="mt-3 text-sm text-ink-mid font-medium leading-relaxed">{tagline}</p>

                  {/* Price */}
                  <div className="mt-6 flex items-baseline gap-1.5">
                    <span className="text-5xl font-extrabold text-ink tracking-tight tabular-nums">
                      {formatINR(p.amount_paise)}
                    </span>
                    <span className="text-base text-ink-soft font-medium">
                      / {p.interval === "yearly" ? "year" : "month"}
                    </span>
                  </div>
                  {savings && (
                    <div className="mt-3 inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-pine/10 text-pine font-bold tracking-tight">
                      {savings}
                    </div>
                  )}

                  <div className="my-6 h-px bg-[#EEF0F2]" />

                  <ul className="space-y-3">
                    {highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-ink-mid font-medium leading-relaxed">
                        <span className={`mt-0.5 grid place-items-center w-4 h-4 rounded-full shrink-0 ${featured ? "bg-pine text-white" : "bg-pine/10 text-pine"}`}>
                          <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                        </span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  <button onClick={() => choose(p.id)} data-testid={`btn-select-${p.id}`}
                          className={`w-full mt-8 rounded-full py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
                            featured
                              ? "bg-ink text-white hover:bg-ink-mid shadow-sm"
                              : "bg-white border border-ink text-ink hover:bg-ink hover:text-white"
                          }`}>
                    Continue with {p.name.replace(" (Yearly)", "")}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-ink-soft mt-6 text-center">Your data is safe for 30 days after trial end. Pick a plan any time to resume where you left off.</p>
        </div>
      </main>
    </div>
  );
}
